<script setup lang="js">
  import Lucide from "@/base-components/Lucide";
  import Button from "@/base-components/Button";
  import {Dialog} from "@/base-components/Headless";
  import {currencyReadable, formatNumber} from "@/composables/utility.js";
  import {ref} from "vue";
  import {useFetch} from "@/composables/useFetch.js";
  import {uri} from "@/constants/config.js";
  import {toast} from "vue3-toastify";

  const {isOpen, promotion} = defineProps(['isOpen', 'promotion']);
  const emits = defineEmits(['close']);
  const isLoading = ref(false)
  const selectedItem = ref(null)

  function selectItem(item) {
    if (item.remain > 0) {
      selectedItem.value = item;
    }
  }

  async function useItem() {
    if (!selectedItem.value) return;
    isLoading.value = true;
    const {data} = await useFetch(uri.api.promotion.use(selectedItem.value.id), {method: "POST"})
    if (data && data.status === true) {
      toast(data.message, {
        theme: "auto",
        type: "success",
        dangerouslyHTMLString: false,
      });
    } else {
      isLoading.value = false;
      return;
    }
    emits('close');
  }
</script>

<template>
  <Dialog
    :open="isOpen"
    @close="emits('close')"
    staticBackdrop="true"
    :size="'w-[95vw]'"
  >
    <Dialog.Panel
      class="bg-[#0d1b2a] text-white font-sans p-6 max-h-full overflow-y-auto"
    >
      <free-style-shimmer
        height="40rem"
        width="100"
        v-if="isLoading"
        color="#0d1b2a"
      />
      <div v-if="!promotion">
        <div class="p-5 text-center">
          <Lucide icon="XCircle" class="w-16 h-16 mx-auto mt-3 text-warning" />
          <div class="mt-5 text-3xl">
            در حال حاضر هیچ داده رویدادی وجود ندارد
          </div>
        </div>
      </div>
      <div v-if="promotion && !isLoading" class="">
        <div class="text-center mb-10">
          <Lucide
            :icon="promotion.icon || 'Gift'"
            class="w-16 h-16 mx-auto mt-3 text-success"
          />
          <h1 class="text-2xl md:text-3xl font-bold">{{ promotion.name }}</h1>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          <div
            v-for="item in promotion.PromotionItems"
            :key="item.id"
            class="bg-[#1b263b] rounded-xl p-6 shadow-md text-center relative transition"
            :class="{
              'opacity-60': item.remain === 0,
              'hover:ring hover:ring-blue-300 cursor-pointer': item.remain > 0,
              border: item === selectedItem,
            }"
            @click="selectItem(item)"
          >
            <div class="text-blue-300 text-5xl mb-4 text-center">
              <Lucide
                :icon="item.icon || 'Compass'"
                class="inline-block w-24 h-24"
              />
            </div>

            <h2 class="text-xl font-bold mb-2">{{ item.title }}</h2>
            <p class="text-blue-200 font-semibold mb-4">
              {{ currencyReadable(item.budget) }} ریال اعتبار هدیه
            </p>
            <p class="text-sm leading-6 mb-4" v-html="item.description"></p>
            <p
              v-if="item.remain === 0"
              class="text-sm text-gray-400 line-through"
            >
              دسترسی موجود نیست
            </p>
            <p v-else class="text-sm text-gray-300">
              فقط {{ formatNumber(item.remain) }} دسترسی موجود است
            </p>
            <div
              v-if="item.remain === 0"
              class="absolute top-0 right-0 bg-red-600 text-white text-xs px-2 py-1 rounded-bl-xl"
            >
              پایان یافته
            </div>
          </div>
        </div>
      </div>
      <Dialog.Footer class="text-center" v-if="!isLoading">
        <div class="flex justify-center items-center gap-6 mt-20 align-middle">
          <Button
            type="button"
            variant="primary"
            @click="useItem"
            class="w-24 mt-2 mb-2 dark"
            :disabled="!selectedItem"
          >
            بزن بریم
          </Button>
          <Button
            type="button"
            variant="outline-warning"
            @click="emits('close')"
            class="w-28 mt-2 mb-2"
          >
            فعلا نمیخوام!
          </Button>
        </div>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>

<style scoped></style>
